// chemistry-lit-element.ts
import { ASS } from "@adornis/ass/index.js";
import { logger } from "@adornis/base/logging.js";
import { adoptStyles, LitElement } from "lit";
import { BehaviorSubject, filter, skip } from "rxjs";
import { DesignController } from "./controllers/design-controller.js";
import { RXController } from "./controllers/RXController.js";
import "./elements/theming/design.js";
import { globalStyle } from "./elements/theming/global-style.js";
var ChemistryLitElement = class extends LitElement {
  constructor() {
    super(...arguments);
    this._connected = new BehaviorSubject(false);
    this.disconnected = this._connected.pipe(
      skip(1),
      filter((x) => !x)
    );
    // INLINE ACSS STYLES
    this.inlineStyleParts = /* @__PURE__ */ new Set();
    // LITE ELEMENT STYLES
    this._liteElementStyleSheets = [];
    this.design = new DesignController(this);
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    this._propertyEventHandling(changedProperties);
    this._afterUpdateStyleHandling();
  }
  connectedCallback() {
    super.connectedCallback();
    this._connected.next(true);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._connected.next(false);
  }
  // REACTIVE PROPERTIES
  static _isRerenderController(maybeController) {
    return maybeController && (maybeController instanceof RXController || maybeController.host && maybeController.propertyName);
  }
  static getPropertyDescriptor(name, key, options) {
    const defaultDescriptor = super.getPropertyDescriptor(name, key, options);
    return {
      get: defaultDescriptor.get,
      set(newValue) {
        const currentValue = this[name];
        if (!currentValue)
          defaultDescriptor.set.call(this, newValue);
        else if (currentValue.constructor && currentValue.constructor === newValue?.constructor)
          defaultDescriptor.set.call(this, newValue);
        else if (this.constructor._isRerenderController(currentValue)) {
          if (currentValue instanceof RXController) {
            if (currentValue.sourceIsNextable) {
              const oldValue = currentValue.value;
              currentValue.source.next(newValue);
              this.requestUpdate(name, oldValue);
            } else {
              logger.warn(
                { propertyName: name, nodeName: this.nodeName },
                "Trying to set non-nextable property from the outside, this will be ignored."
              );
            }
          } else {
            const oldValue = currentValue.getValue();
            currentValue.next(newValue);
            this.requestUpdate(name, oldValue);
          }
        } else
          defaultDescriptor.set.call(this, newValue);
      },
      configurable: true,
      enumerable: true
    };
  }
  static {
    // PROPERTY EVENTS
    this._eventProperties = /* @__PURE__ */ new Set();
  }
  _propertyEventHandling(changedProperties) {
    const constr = this.constructor;
    Array.from(changedProperties.keys()).forEach((key) => {
      if (constr._eventProperties.has(key)) {
        const value = this.constructor._isRerenderController(this[key]) ? this[key].value : this[key];
        this.dispatchEvent(new CustomEvent(`${key}-changed`, { detail: { value } }));
      }
    });
  }
  // COMPONENT STYLING
  styles() {
    return [];
  }
  _afterUpdateStyleHandling() {
    if (this.styles) {
      let styles = this.styles();
      if (!Array.isArray(styles))
        styles = [styles];
      const acssArray = styles.map((style) => ASS.makeSheet(style));
      const sheets = acssArray.map((acss) => acss.toSheet());
      if (!this._currentComponentStyles || sheets.length !== this._currentComponentStyles.length || sheets.some((sheet, i) => sheet !== this._currentComponentStyles[i]))
        this._currentComponentStyles = sheets;
    }
    this._updateStyles();
  }
  /**
   * @deprecated only for internal use
   */
  _updateStyles() {
    adoptStyles(this.renderRoot, [
      ...globalStyle.getValue().map((style) => ASS.makeSheet(style()).toSheet()),
      ...this._liteElementStyleSheets.map((styleFn) => styleFn()).flat().map((style) => ASS.makeSheet(style).toSheet()),
      ...this._currentComponentStyles ? this._currentComponentStyles : [],
      ...new Set([...this.inlineStyleParts].map((part) => part.sheet).filter(Boolean))
    ]);
  }
  addLiteElementStyleSheet(styleSheetFn) {
    if (this._liteElementStyleSheets.includes(styleSheetFn))
      return;
    this._liteElementStyleSheets.push(styleSheetFn);
    if (this.hasUpdated)
      this._updateStyles();
  }
  removeLiteElementStyleSheet(styleSheet) {
    const styleSheetIndex = this._liteElementStyleSheets.indexOf(styleSheet);
    if (styleSheetIndex === -1)
      return;
    this._liteElementStyleSheets.splice(styleSheetIndex, 1);
  }
};
export {
  ChemistryLitElement
};
